import React from 'react'

interface Props {
  fallbackTitle?: string
  children: React.ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches render/runtime errors in its children and shows a friendly
 * fallback instead of a blank white screen. The fallback offers a reload
 * so the user can recover without losing the page URL.
 *
 * Only class components can be error boundaries in React. Keep this
 * component minimal so it is itself unlikely to crash.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info)
  }

  reload = () => {
    // Hard reload — clears any partially broken state.
    window.location.reload()
  }

  render() {
    if (this.state.error) {
      const title = this.props.fallbackTitle || 'Something went wrong'
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'Segoe UI, Arial, sans-serif',
            background: '#f2f2f2',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: '36px 32px',
              maxWidth: 480,
              width: '100%',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ margin: '0 0 10px 0', color: '#242424' }}>{title}</h2>
            <p style={{ color: '#616161', fontSize: 14, margin: '0 0 20px 0' }}>
              An unexpected error occurred. Please reload and try again. If it keeps happening,
              let your agency know.
            </p>
            <button
              onClick={this.reload}
              style={{
                background: '#0F6CBD',
                color: '#fff',
                border: 0,
                padding: '10px 22px',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <details style={{ marginTop: 16, color: '#8a8886', fontSize: 12 }}>
              <summary style={{ cursor: 'pointer' }}>Technical details</summary>
              <pre
                style={{
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  marginTop: 8,
                }}
              >
                {this.state.error.message}
              </pre>
            </details>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
